<template>
  <b-modal
    ref="detail-modal"
    hide-footer
    :title="modalTitle"
    size="lg"
    @hidden="onModalHidden"
  >
    <v-form>
      <div>
        <div class="mb-4">
          <p>
            <span class="font-weight-bold">Đường dẫn: </span>
            {{ hrmSyncStatus.url }}
          </p>
          <p>
            <span class="font-weight-bold">Phương thức: </span>
            <b-badge variant="info">{{ hrmSyncStatus.method }}</b-badge>
          </p>
          <p v-if="hrmSyncStatus.errorFrom">
            <span class="font-weight-bold">Nguồn lỗi: </span>
            <b-badge variant="danger">{{ hrmSyncStatus.errorFrom }}</b-badge>
          </p>
          <b-form-group
            label="Thời gian tạo/ cập nhật"
            label-class="font-weight-bold"
          >
            {{ formatDate(hrmSyncStatus.createdAt, CLIENT_DATE_TIME) }}
            <i class="text-dark-75 fa fa-arrows-alt-h mx-2"></i>
            {{ formatDate(hrmSyncStatus.updatedAt, CLIENT_DATE_TIME) }}
          </b-form-group>
        </div>

        <p v-if="hrmSyncStatus.retryCount">
          <span class="font-weight-bold">Số lần thử lại: </span>
          <span class="font-weight-bold">{{ hrmSyncStatus.retryCount }}</span>
        </p>

        <div class="mb-4">
          <p>
            <span class="font-weight-bold">Kết quả lần đầu: </span>
            {{ hrmSyncStatus.response || '' }}
          </p>
        </div>

        <b-form-group
          label="Đầu vào gốc"
          label-for="original-payload"
          label-class="font-weight-bold"
        >
          <pre id="original-payload" class="payload-display">{{
            formatPayload(hrmSyncStatus.payload)
          }}</pre>
        </b-form-group>

        <template v-if="isRetryMode">
          <div class="mb-4">
            <p>
              <span class="font-weight-bold">Kết quả lần đầu: </span>
              {{ hrmSyncStatus.response || '' }}
            </p>
          </div>
          <b-form-group
            label="Đầu vào thử lại"
            label-for="retry-payload"
            label-class="font-weight-bold"
          >
            <b-form-textarea
              id="retry-payload"
              v-model="retryPayloadText"
              rows="10"
              placeholder="Nhập JSON để cập nhật"
              class="font-monospace"
              :class="{ 'is-invalid': !isValidRetryPayload }"
            ></b-form-textarea>
            <div v-if="!isValidRetryPayload" class="invalid-feedback">
              JSON không hợp lệ
            </div>
          </b-form-group>
        </template>

        <template v-else-if="hasRetryPayload">
          <div class="mb-4">
            <p>
              <span class="font-weight-bold">Kết quả lần đầu: </span>
              {{ hrmSyncStatus.response || '' }}
            </p>
          </div>
          <b-form-group
            label="Đầu vào thử lại"
            label-for="existing-retry-payload"
            label-class="font-weight-bold"
          >
            <pre id="existing-retry-payload" class="payload-display">{{
              formatPayload(hrmSyncStatus.retryPayload)
            }}</pre>
          </b-form-group>
        </template>

        <div class="d-flex justify-content-end mt-4">
          <b-button
            v-if="isRetryMode"
            variant="success"
            size="sm"
            class="mr-2"
            :disabled="isSubmitting || !isValidRetryPayload"
            @click="handleRetry"
          >
            <i class="fa fa-spinner fa-spin mr-1" v-if="isSubmitting"></i>
            Đồng bộ lại
          </b-button>

          <b-button
            v-if="!isRetryMode && canRetry"
            variant="warning"
            size="sm"
            class="mr-2"
            @click="onSubmit"
          >
            Đồng bộ lại
          </b-button>

          <b-button variant="secondary" size="sm" @click="hideModal">
            {{ isRetryMode ? 'Hủy' : 'Đóng' }}
          </b-button>
        </div>
      </div>
    </v-form>
  </b-modal>
</template>

<script>
import { formatDate, CLIENT_DATE_TIME } from '@/utils/date';
import ApiService from '@/core/services/api.service';
import { STATUS_CODE } from '@/utils/enum';
import { makeToastFaile, makeToastSuccess, handleError } from '@/utils/common';

export default {
  name: 'HRMSyncDetailModal',
  props: {
    hrmSyncStatus: {
      type: Object,
      default: () => ({
        payload: null,
        url: null,
        method: null,
        createdAt: null,
        updatedAt: null,
        retryResponse: null,
        response: null,
        retryPayload: null,
      }),
    },
    mode: {
      type: String,
      default: 'view',
      validator: (value) => ['view', 'retry'].includes(value),
    },
  },

  data() {
    return {
      CLIENT_DATE_TIME,
      isRetryMode: this.mode === 'retry',
      retryPayloadText: '',
      isSubmitting: false,
    };
  },

  computed: {
    modalTitle() {
      return this.isRetryMode ? 'Đồng bộ lại' : 'Chi tiết lỗi';
    },

    canRetry() {
      return this.hrmSyncStatus.status === STATUS_CODE.ERROR;
    },

    hasRetryPayload() {
      return (
        this.hrmSyncStatus.retryPayload &&
        this.hrmSyncStatus.retryPayload.trim()
      );
    },

    isValidRetryPayload() {
      if (!this.retryPayloadText.trim()) return false;

      try {
        JSON.parse(this.retryPayloadText);
        return true;
      } catch (error) {
        return false;
      }
    },
  },

  watch: {
    hrmSyncStatus: {
      handler(newHrmSyncStatus) {
        this.initRetryPayload(newHrmSyncStatus);
      },
      immediate: true,
    },

    mode(newMode) {
      this.isRetryMode = newMode === 'retry';
      if (this.isRetryMode) {
        this.initRetryPayload(this.hrmSyncStatus);
      }
    },
  },

  methods: {
    formatDate,
    formatPayload(payloadString) {
      if (!payloadString || typeof payloadString !== 'string') {
        return '';
      }

      try {
        const parsed = JSON.parse(payloadString);
        return JSON.stringify(parsed, null, 2);
      } catch (error) {
        return payloadString;
      }
    },
    initRetryPayload(newHrmSyncStatus) {
      if (!newHrmSyncStatus) return;
      const sourcePayload =
        newHrmSyncStatus.retryPayload || newHrmSyncStatus.payload;
      if (sourcePayload) {
        this.retryPayloadText = this.formatPayload(sourcePayload);
      } else {
        this.retryPayloadText = '';
      }
    },
    validateRetryPayload() {
      const trimmedPayload = this.retryPayloadText.trim();

      if (!trimmedPayload) {
        makeToastFaile('Vui lòng nhập payload để đồng bộ lại');
        return false;
      }

      try {
        return JSON.parse(trimmedPayload);
      } catch (error) {
        makeToastFaile('Payload không đúng định dạng JSON');
        return false;
      }
    },

    showModal() {
      this.$refs['detail-modal'].show();
    },

    hideModal() {
      this.$refs['detail-modal'].hide();
    },

    onSubmit() {
      this.isRetryMode = true;
      this.initRetryPayload(this.hrmSyncStatus);
      this.$emit('mode-changed', 'retry');
    },

    async handleRetry() {
      const retryPayload = this.validateRetryPayload();
      if (!retryPayload) return;

      this.isSubmitting = true;

      try {
        const {
          data: { data },
        } = await ApiService.put(
          `hrm-sync/retry/${this.hrmSyncStatus.id}`,
          retryPayload,
        );

        Object.assign(this.hrmSyncStatus, data);
        const hrmSyncStatusUpdated = { ...this.hrmSyncStatus, ...data };
        this.$emit('retry-success', hrmSyncStatusUpdated);

        this.isRetryMode = false;
        this.$emit('mode-changed', 'view');

        this.hideModal();
        makeToastSuccess('Đồng bộ lại thành công');
      } catch (error) {
        await handleError(error);
      } finally {
        this.isSubmitting = false;
      }
    },

    onModalHidden() {
      if (this.isRetryMode) {
        this.isRetryMode = false;
        this.$emit('mode-changed', 'view');
      }
      this.$emit('modal-hidden');
    },
  },
};
</script>

<style scoped>
.payload-display {
  margin: 1em 0;
  padding: 1em;
  border: 1px solid #bbb;
  display: block;
  background: #f8f9fa;
  border-radius: 3px;
  font-size: 0.875rem;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'Courier New', Courier, monospace;
}

.font-monospace {
  font-family: 'Courier New', Courier, monospace;
}

.form-group {
  margin-bottom: 1em;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}
</style>
